/**
 * Which venues a user may reach.
 *
 * Kept separate from `Capability` on purpose: a capability is a static question
 * ("may this user configure bookings?") answered by the policy table. Venue
 * access is *parameterised by data* — "may this user reach serial FI2EOLUNB3EZ?"
 * — so it cannot be a member of an enumerable union. Same engine, different
 * shape of question.
 *
 * Pure and dependency-free so it can be used from both the access subject and
 * `useLocations` without an import cycle, and tested in a node environment.
 *
 * ⚠️ CLIENT-SIDE ONLY. Filtering a venue switcher is presentation, not access
 * control: the API will still serve a venue the user was never shown. This is a
 * deliberate, known short-term gap — the backend middleware is a separate piece
 * of work. Do not mistake this module for a security boundary.
 */

/** The shape we need off `User['location_access']`. */
interface LocationAccessRow {
  serial: string
}

/**
 * Serials this user is restricted to, or `null` for "no restriction".
 *
 * The distinction matters and is the established convention in this codebase
 * (see the filters this replaces in `venues/index.tsx` and `venues/new.tsx`):
 * an absent or empty `location_access` means the user is NOT restricted and may
 * see every venue in the org, which is the normal case for owners and admins.
 * Only a populated list narrows anything.
 *
 * Returning `null` rather than an empty array keeps "unrestricted" and
 * "restricted to nothing" distinguishable, so a future explicit no-venues state
 * cannot be silently read as full access.
 */
export function resolveAccessibleSerials(
  locationAccess: LocationAccessRow[] | undefined | null
): string[] | null {
  if (!locationAccess?.length) return null
  const serials = locationAccess
    .map((row) => row.serial)
    .filter((serial): serial is string => Boolean(serial))
  return serials.length > 0 ? serials : null
}

/** May this user reach this serial? `null` accessible ⇒ unrestricted. */
export function canAccessSerial(
  accessibleSerials: string[] | null,
  serial: string | undefined
): boolean {
  if (!serial) return true
  if (accessibleSerials === null) return true
  return accessibleSerials.includes(serial)
}

/**
 * Narrow a list of venues to the ones this user may reach.
 *
 * Applied at the venue-list SOURCE (`useLocations`) rather than per picker, so
 * every switcher, select and venue grid inherits it by construction. Fixing
 * pickers one at a time is what left the nav's `VenueSwitcher` showing every
 * venue in the org while `venues/index.tsx` filtered correctly.
 */
export function filterVenuesByAccess<T extends { serial?: string | null }>(
  venues: T[],
  accessibleSerials: string[] | null
): T[] {
  if (accessibleSerials === null) return venues
  const allowed = new Set(accessibleSerials)
  return venues.filter((venue) => venue.serial && allowed.has(venue.serial))
}

/**
 * Where to send someone who asked for a venue they cannot reach.
 *
 * A redirect, not a 403: this fires on stale bookmarks and shared links as much
 * as on anything untoward, and a restricted user landing on their own venue is
 * a better outcome than an error page. Returns undefined when there is nowhere
 * sensible to go, in which case the caller should show the no-access state.
 */
export function firstAccessibleSerial(
  venues: { serial?: string | null }[],
  accessibleSerials: string[] | null
): string | undefined {
  const allowed = filterVenuesByAccess(venues, accessibleSerials)
  return allowed[0]?.serial ?? undefined
}
